import { useInput } from "@redotech/react-util/form";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { toast } from "@redotech/redo-web/alert";
import {
  groupInput,
  input,
  InputProvider,
  nonEmptyValidator,
} from "@redotech/ui/form";
import { phoneNumberValidator } from "@redotech/ui/phone";
import { assertNever } from "@redotech/util/type";
import { emailValidator } from "@redotech/web-util/form";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SetAuthContext } from "../contexts/auth";
import { SettingsContext } from "../contexts/settings";
import { SetUserContext } from "../contexts/user";
import { getLoginGiftText } from "./dynamic-text-utils";

const giftInput = groupInput({
  buyerEmailOrZip: input<string>({ validator: nonEmptyValidator }),
  resourceId: input<string>({ validator: nonEmptyValidator }),
  firstName: input<string>({ validator: nonEmptyValidator }),
  lastName: input<string>({ validator: nonEmptyValidator }),
  email: input<string>({ validator: emailValidator }),
  phoneNumber: input<string>({ validator: phoneNumberValidator }),
  street1: input<string>({ validator: nonEmptyValidator }),
  street2: input<string>(),
  city: input<string>({}),
  state: input<string>({ validator: nonEmptyValidator }),
  zip: input<string>({ validator: nonEmptyValidator }),
  country: input<string>({}),
});
type BaseInputFields = Parameters<typeof giftInput>[0];
type InputFieldsInfo = {
  [K in keyof BaseInputFields]: {
    label: string;
    errorDescription: string | undefined;
  };
};

export enum GiftModalPage {
  GIFT_RECEIPT_REQUEST = "gift-receipt-request",
  GIFT_RECEIPT_REQUEST_SUCCESS = "gift-receipt-request-success",
}

export interface UseGiftResponse {
  texts: {
    title: string;
    subtitle: string;
    merchantName: string;
    buyerInfo: string;
    yourInfo: string;
    findOrder: string;
    missingBuyerInfoButton: string;
    modal: {
      title: string;
      subtitle: string;
      emailLabel: string;
      emailPlaceholder: string;
      informationLabel: string;
      informationPlaceHolder: string;
      primaryButton: string;
      secondaryButton: string;
    };
  };
  showMissingBuyerInfoButton: boolean;
  inputFields: InputProvider.Form<typeof giftInput>;
  inputFieldInfos: InputFieldsInfo;
  findOrderDisabled: boolean;
  findOrderClicked: () => void;
  findOrderLoad: LoadState<void>;
  modal: {
    isOpen: boolean;
    contactEmail: string;
    giftDetails: string;
    setContactEmail: (email: string) => void;
    setGiftDetails: (details: string) => void;
    openModal: () => void;
    closeModal: () => void;
    onSubmit: () => void;
    page: GiftModalPage;
    primaryButton: {
      onClick: () => void;
      disabled: boolean;
      pending: boolean;
      text: string;
    };
    secondaryButton: { onClick: () => void; disabled: boolean; show: boolean };
  };
}

export function useGift(): UseGiftResponse {
  const settings = useContext(SettingsContext);
  const rpcClient = useContext(RpcClientContext);
  const setAuth_ = useContext(SetAuthContext);
  const setUser = useContext(SetUserContext);
  const navigate = useNavigate();
  const inputFields = useInput(giftInput, {
    buyerEmailOrZip: "",
    resourceId: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    country: "United States",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(GiftModalPage.GIFT_RECEIPT_REQUEST);
  const [contactEmail, setContactEmail] = useState("");
  const [giftDetails, setGiftDetails] = useState("");

  const [findOrderLoad, triggerFindOrder] = useTriggerLoad(async () => {
    if (!rpcClient || !setAuth_ || !setUser) {
      return;
    }
    const { customerAuthToken, customer, orderId } = await rpcClient.giftAuth({
      gifterEmail: inputFields.value.buyerEmailOrZip,
      orderIdTrackingNumber: inputFields.value.resourceId,
    });
    const address = {
      street1: inputFields.value.street1,
      street2: inputFields.value.street2 || "",
      city: inputFields.value.city,
      state: inputFields.value.state,
      country: inputFields.value.country,
      zip: inputFields.value.zip,
      phone: inputFields.value.phoneNumber,
      firstName: inputFields.value.firstName,
      lastName: inputFields.value.lastName,
    };
    setAuth_(customerAuthToken);
    setUser({
      name: customer.name,
      email: customer.email,
      guestEmail: inputFields.value.email.toLowerCase().trim(),
      guestName: inputFields.value.firstName + " " + inputFields.value.lastName,
      address: JSON.stringify(address),
    });

    navigate(`/returns-portal/orders/${orderId}`, { replace: true });
  });

  function getErrorDescription(inputName: keyof BaseInputFields) {
    const inputField = inputFields.inputs[inputName];
    if (inputField.changed && inputField.errors.length > 0) {
      return inputField.errors[0];
    }
    return undefined;
  }

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setContactEmail("");
    setGiftDetails("");
    setPage(GiftModalPage.GIFT_RECEIPT_REQUEST);
  };

  const [submitGiftReceiptRequestLoad, doSubmitGiftReceiptRequest] =
    useTriggerLoad(async () => {
      if (!rpcClient) {
        return;
      }
      try {
        await rpcClient.createGiftReceiptRequest({
          email: contactEmail,
          giftInformation: giftDetails,
        });
        setPage(GiftModalPage.GIFT_RECEIPT_REQUEST_SUCCESS);
      } catch (error) {
        toast("Error submitting gift return request. Please try again later.", {
          variant: "error",
        });
        return;
      }
    });

  const headerText: Record<GiftModalPage, string> = {
    [GiftModalPage.GIFT_RECEIPT_REQUEST]: "Missing buyer's information",
    [GiftModalPage.GIFT_RECEIPT_REQUEST_SUCCESS]: "Request received",
  };
  const subtitleText: Record<GiftModalPage, string> = {
    [GiftModalPage.GIFT_RECEIPT_REQUEST]:
      settings?.returns?.missingBuyerInfoDescription ?? "",
    [GiftModalPage.GIFT_RECEIPT_REQUEST_SUCCESS]:
      "Thank you for this information. We will be in touch with you soon to assist with your return",
  };
  const primaryButtonText: Record<GiftModalPage, string> = {
    [GiftModalPage.GIFT_RECEIPT_REQUEST]: "Submit",
    [GiftModalPage.GIFT_RECEIPT_REQUEST_SUCCESS]: "Close",
  };

  return {
    texts: {
      title: getLoginGiftText(settings),
      subtitle:
        "Enter the buyer information and your information to find your order.",
      merchantName: settings?.merchantName || "Unknown Merchant",
      buyerInfo: "Buyer info:",
      yourInfo: "Your info:",
      findOrder: "Find order",
      missingBuyerInfoButton: "Missing buyer's info?",
      modal: {
        title: headerText[page],
        subtitle: subtitleText[page],
        emailLabel: "Your email",
        emailPlaceholder: "Enter email...",
        informationLabel: "Gift information",
        informationPlaceHolder: "Enter details about the gift and purchaser...",
        primaryButton: primaryButtonText[page],
        secondaryButton: "Cancel",
      },
    },
    showMissingBuyerInfoButton: !!settings?.returns?.enableGiftReceipts,
    inputFields,
    inputFieldInfos: {
      buyerEmailOrZip: {
        label: `${settings?.emailLoginLabelText || "Email or Zip code"} *`,
        errorDescription: getErrorDescription("buyerEmailOrZip"),
      },
      resourceId: {
        label: `${settings?.orderNumberLoginLabelText || "Order # or Tracking #"} *`,
        errorDescription: getErrorDescription("resourceId"),
      },
      firstName: {
        label: "First name *",
        errorDescription: getErrorDescription("firstName"),
      },
      lastName: {
        label: "Last name *",
        errorDescription: getErrorDescription("lastName"),
      },
      email: {
        label: "Email *",
        errorDescription: getErrorDescription("email"),
      },
      phoneNumber: {
        label: "Phone number",
        errorDescription: getErrorDescription("phoneNumber"),
      },
      street1: {
        label: "Address *",
        errorDescription: getErrorDescription("street1"),
      },
      street2: {
        label: "Apt, suite, etc. (optional)",
        errorDescription: getErrorDescription("street2"),
      },
      city: { label: "City", errorDescription: getErrorDescription("city") },
      state: {
        label: "State *",
        errorDescription: getErrorDescription("state"),
      },
      zip: {
        label: "Zip code *",
        errorDescription: getErrorDescription("zip"),
      },
      country: {
        label: "Country",
        errorDescription: getErrorDescription("country"),
      },
    },
    findOrderDisabled:
      inputFields.allErrors.length > 0 ||
      inputFields.changed === false ||
      findOrderLoad.pending,
    findOrderClicked: triggerFindOrder,
    findOrderLoad,
    modal: {
      isOpen: isModalOpen,
      contactEmail,
      giftDetails,
      setContactEmail,
      setGiftDetails,
      openModal,
      closeModal,
      onSubmit: doSubmitGiftReceiptRequest,
      page,
      primaryButton: {
        onClick: () => {
          switch (page) {
            case GiftModalPage.GIFT_RECEIPT_REQUEST:
              doSubmitGiftReceiptRequest();
              break;
            case GiftModalPage.GIFT_RECEIPT_REQUEST_SUCCESS:
              closeModal();
              break;
            default:
              assertNever(page);
          }
        },
        disabled: !contactEmail.trim() || !giftDetails.trim(),
        pending: submitGiftReceiptRequestLoad.pending,
        text: primaryButtonText[page],
      },
      secondaryButton: {
        onClick: closeModal,
        disabled: false,
        show: page === GiftModalPage.GIFT_RECEIPT_REQUEST,
      },
    },
  };
}
